import { useEffect, useMemo, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import Loader from "Components/Base/BaseLoader";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
    JOB_ID,
    REPORTED_ISSUE,
    CUSTOMER_NAME,
    TECHNICIANS,
    PRIORITY_LEVEL,
    STATUS,
    WARRANTY_START_DATE,
    WARRANTY_END_DATE,
    START_DATE,
    COMPLETION_DATE,
    getPaymentStatusColor,
    priorities,
    statuses
} from '../../Components/constants/Reports';
import { exportJobReport, JobCardReports } from "Api/reposts";
import { checkStatusCodeSuccess, deleteUploadedFile, notFound } from "Components/constants/Common";
import { getCustomerList } from "Api/jobcard";
import { RiSearch2Line } from "react-icons/ri";
import { BaseImageURL } from "Api/services";
import { END_DATE, END_DATE_LABEL } from "Components/constants/JobCard";
import { SelectPlaceHolder } from "Components/constants/validation";
import { use } from "i18next";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import moment from "moment";

interface Product {
    id: number;
    product_code: string;
}

interface JobCardProduct {
    id: number;
    product_id: number;
    quantity: number;
    rate: number;
    total_amount: number;
    product: Product;
}

interface Technician {
    id: number;
    technician_name: string;
}

interface AuthUser {
    id: number;
    name: string;
}

interface JobCard {
    id: number;
    reported_issue: string;
    auth_id: number;
    technician_ids: string;
    warranty_start_date: string;
    warranty_end_date: string;
    start_date: string;
    completion_date: string;
    priority_level: string;
    status: string;
    authUser: AuthUser;
    jobCardProduct: JobCardProduct[];
    technician: Technician[];
}

interface Pagination {
    totalItems: number;
    totalPages: number;
    page: number;
    pageSize: number;
}

interface ApiResponse {
    statusCode: number;
    status: string;
    data: {
        orders: JobCard[];
        pagination: Pagination;
    };
}

const JobCardReport: React.FC = () => {
    document.title = "Job Card Report";

    const [jobCardData, setJobCardData] = useState<JobCard[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [customPageSize, setCustomPageSize] = useState<number>(10);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnName, setColumnName] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<string>("asc");

    // State for filtering
    const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [customerName, setCustomerName] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
    const [respStatus, setRespStatus] = useState<number | null>(null);
    const [error,setError] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const handleFetchData = (page: any) => {
        setPage(page);
    };

    const jobCardColumns = useMemo(() => [
        {
            header: JOB_ID,
            accessorKey: "id",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>J{cell.row.original.id || notFound.nullData}</span>;
            },
        },
        {
            header: REPORTED_ISSUE,
            accessorKey: "reported_issue",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.reported_issue || notFound.nullData}</span>;
            },
        },
        {
            header: CUSTOMER_NAME,
            accessorKey: "authUser.name",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.authUser?.name || notFound.nullData}</span>;
            },
        },
        {
            header: TECHNICIANS,
            accessorKey: "technician",
            cell: (cell: any) => {
                const value = cell.row.original.technician;
                if (Array.isArray(value) && value.length > 0) {
                    return (
                        <div>
                            {value.length}
                        </div>
                    );
                }
                return <span>{notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: PRIORITY_LEVEL,
            accessorKey: "priority_level",
            cell: (cell: any) => {
                const value = cell.row.original.priority_level;
                const backgroundColor = getPaymentStatusColor(value || notFound.nullData);
                return <span className="badge p-1 rounded text-white" style={{ ...backgroundColor }}>{value || notFound.nullData}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: STATUS,
            accessorKey: "status",
            cell: (cell: any) => {
                const value = cell.row.original.status;
                const backgroundColor = getPaymentStatusColor(value || notFound.nullData);
                return <span className="badge p-1 rounded text-white" style={{ ...backgroundColor }}>{value || notFound.nullData}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: WARRANTY_START_DATE,
            accessorKey: "warranty_start_date",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                const value = cell.row.original.warranty_start_date;
                return (
                    <div>
                        <span>
                            {value ? moment(value).format("ll") : notFound.nullData}
                        </span>
                    </div>
                );
            },
        },
        {
            header: WARRANTY_END_DATE,
            accessorKey: "warranty_end_date",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                const value = cell.row.original.warranty_end_date;
                return (
                    <div>
                        <span>
                            {value ? moment(value).format("ll") : notFound.nullData}
                        </span>
                    </div>
                );
            },
        },
        {
            header: START_DATE,
            accessorKey: "start_date",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                const value = cell.row.original.start_date;
                return (
                    <div>
                        <span>
                            {value ? moment(value).format("ll") : notFound.nullData}
                        </span>
                    </div>
                );
            },
        },
        {
            header: COMPLETION_DATE,
            accessorKey: "completion_date",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                const value = cell.row.original.completion_date;
                return (
                    <div>
                        <span>
                            {value ? moment(value).format("ll") : notFound.nullData}
                        </span>
                    </div>
                );
            },
        },
    ], []);

    const fetchCustomers = async () => {
        try {
            const response = await getCustomerList();
            if (response.data && checkStatusCodeSuccess(response.status)) {
                setRespStatus(response.status);
                setCustomerName(response.data);
            } else {
                console.error("Failed to fetch customers:", response);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchJobCardData = async () => {
        setError(false);
        setLoader(true);
        const payload = {
            pageSize: customPageSize,
            page: page,
            "sortValue": "asc",
            "sortKey": "id",
            search: searchValue,
            priority_level: selectedPriority,
            status: selectedStatus,
            start_date: startDate,
            end_date: endDate,
            customer_name: selectedCustomer,
        };

        try {
            const response = await JobCardReports(payload);
            if (response.data && checkStatusCodeSuccess(response.status)) {
                const data = response.data.data;
                setJobCardData(data.orders || []);
                setTotalRecords(data.pagination.totalItems);
                setTotalPages(data.pagination.totalPages);
            } else {
                console.error("Failed to fetch job cards:", response);
                setError(true);
            }
        } catch (error) {
            console.error("Error fetching job cards:", error);
            setError(true);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchJobCardData();
    }, [page, customPageSize, columnName, sortOrder, searchValue, selectedPriority, selectedStatus, startDate, endDate, selectedCustomer]);

    const exportFile = async () => {
        setIsExporting(true);
        const payload = {
            priority_level: selectedPriority,
            status: selectedStatus,
            start_date: startDate,
            end_date: endDate,
            customer_name: selectedCustomer,
            // in future need to remove this page size limit
            pageSize: 50 
        };
    
        try {
            const response = await exportJobReport(payload);
            const fileName = response.data.data;
            const fileUrl = `${BaseImageURL}${fileName}`;
            window.open(fileUrl, '_blank');
            
            // Use success message from backend if available
            toast.success(response.data.message || "File exported successfully!");
            if (fileUrl) {
                deleteUploadedFile(fileName)
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                // Use error message from backend if available
                const errorMessage = error.response?.data?.message || "An unexpected error occurred. Please try again later.";
                
                if (error.response?.status === 404) {
                    toast.info(errorMessage);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error("An unexpected error occurred. Please try again later.");
            }
        } finally {
            setIsExporting(false);  // Stop loader when export ends
        }
    
};
    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedCustomer(null);
        setSelectedStatus(null);
        setSelectedPriority(null);
        setPage(1); // Reset to the first page
    };

   


    return (
        <div className="page-content">
            <div className="flex-grow-1 mx-3">
                <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
                    <span className="fs-4">Job Card Report</span>
                    <BaseButton
    className="btn btn-success"
    onClick={exportFile}
    loader={isExporting}  // Pass the loading state here
      // Disable button when exporting
>
    {isExporting ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    ) : (
        <>
            <i className="mdi mdi-download" /> Export
        </>
    )}
</BaseButton>
                </div>

                <div className="card py-2 px-4 overflow-x-auto">
                <div className="container-fluid">
    <div className="row g-3 align-items-end mb-3 justify-content-end">
        {/* Start Date Input */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseInput
                type="date"
                className="form-control"
                label={START_DATE}
                value={startDate || ''}
                onChange={(e) => {
                    setStartDate(e.target.value || null);
                    setPage(1);
                }}
            />
        </div>

        {/* End Date Input */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseInput
                type="date"
                className="form-control"
                label={END_DATE_LABEL}
                value={endDate || ''}
                onChange={(e) => {
                    setEndDate(e.target.value || null);
                    setPage(1);
                }}
            />
        </div>

        {/* Customer Name Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={CUSTOMER_NAME}
                options={customerName.map((customer: any) => ({
                    label: customer.name,
                    value: customer.name,
                }))}
                value={selectedCustomer}
                handleChange={(name: any, value: any) => {
                    setSelectedCustomer(value);
                    setPage(1);
                }}
            />
        </div>

        {/* Priority Level Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={PRIORITY_LEVEL}
                options={priorities}
                value={selectedPriority}
                handleChange={(name: any, value: any) => {
                    setSelectedPriority(value === "" ? null : value);
                    setPage(1);
                }}
            />
        </div>

        {/* Status Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={STATUS}
                options={statuses}
                value={selectedStatus}
                handleChange={(name: any, value: any) => {
                    setSelectedStatus(value === "" ? null : value);;
                    setPage(1);
                }}
            />
        </div>

        {/* Reset Button */}
        <div className="col-12 col-sm-6 col-lg-auto ms-lg-2 d-flex justify-content-end"  style={{ minWidth: '100px' }}>
            <BaseButton
                className="btn btn-danger px-4"
                onClick={resetFilters}
               
            >
                <i className="mdi mdi-refresh me-1" />
                Reset
            </BaseButton>
        </div>
    </div>
</div>


                    {(!error && (loader || jobCardData.length > 0)) ? (
                        <TableContainer
                            customPageSize={customPageSize}
                            setCustomPageSize={setCustomPageSize}
                            isGlobalFilter={true}
                            columns={jobCardColumns}
                            data={jobCardData}
                            tableClass="table bg-white text-nowrap"
                            divClass="text-nowrap"
                            manualPagination={true}
                            manualFiltering={false}
                            tableLoader={loader}
                            hasManualPagination={true}
                            SearchPlaceholder="Search job cards..."
                            currentPage={page}
                            setCurrentPage={setPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            totalNumberOfRows={totalRecords}
                            fetchData={handleFetchData}
                            fetchSortingData={fetchJobCardData}
                        />
                    ) : (
                        <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearch2Line />
                </span>
              </div>
              <div className="mt-4">
                <h5>Job Card Data Not Found</h5>
                <p className="text-muted">The Data is not available</p>
              </div>
            </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobCardReport;
